/**
 * Entity type registry for connection registry operations.
 * Resolves Firestore paths and canonical ordering without hardcoded switches in link services.
 */

export const ENTITY_TYPE_REGISTRY = {
  day: {
    collection: 'days',
    contentField: 'content',
    routeTemplate: '/trips/:tripId/day/:id',
    displayName: 'Day',
    sortRank: 1,
    enabled: true
  },
  accommodation: {
    collection: 'accommodation',
    contentField: 'contentBlocks',
    routeTemplate: '/trips/:tripId/accommodation/:id',
    displayName: 'Accommodation',
    sortRank: 2,
    enabled: true
  },
  place: {
    collection: 'places',
    contentField: 'contentBlocks',
    routeTemplate: '/trips/:tripId/places/:id',
    displayName: 'Place',
    sortRank: 3,
    enabled: true
  },
  travel: {
    collection: 'travel',
    contentField: 'contentBlocks',
    routeTemplate: '/trips/:tripId/travel/:id',
    displayName: 'Travel',
    sortRank: 4,
    enabled: true
  },
  document: {
    collection: 'documents',
    contentField: null,
    routeTemplate: '/trips/:tripId/documents/:id',
    displayName: 'Document',
    sortRank: 5,
    enabled: true
  },
  extra: {
    collection: 'extras',
    contentField: 'contentBlocks',
    routeTemplate: '/trips/:tripId/extras/:id',
    displayName: 'Extra',
    sortRank: 10,
    enabled: false
  }
};

/**
 * Legacy document-connection subtypes → canonical registry entity types.
 * Document entity creation uses subtypes (hotel, flight, etc.); the link registry uses top-level types.
 */
export const ENTITY_TYPE_ALIASES = {
  hotel: 'accommodation',
  apartment: 'accommodation',
  house_villa: 'accommodation',
  resort: 'accommodation',
  hostel: 'accommodation',
  flight: 'travel',
  train: 'travel',
  bus: 'travel',
  car: 'travel',
  cruise: 'travel',
  ferry: 'travel',
  location: 'place'
};

/** @typedef {'day'|'accommodation'|'place'|'travel'|'document'|'extra'} EntityType */

export const LINK_TYPES = {
  LOCATED_IN: 'located_in',
  STAYS_ON: 'stays_on',
  DEPARTS_FROM: 'departs_from',
  ARRIVES_AT: 'arrives_at',
  ASSOCIATED_WITH: 'associated_with',
  DOCUMENT_FOR: 'document_for',
  ALLOCATES_COST: 'allocates_cost'
};

export const LINK_TYPE_DEFAULTS = {
  [LINK_TYPES.STAYS_ON]: { linkIntent: 'selected', displayMode: 'itinerary_entry' },
  [LINK_TYPES.LOCATED_IN]: { linkIntent: 'selected', displayMode: 'related_only' },
  [LINK_TYPES.DOCUMENT_FOR]: { linkIntent: 'selected', displayMode: 'related_only' },
  [LINK_TYPES.ASSOCIATED_WITH]: { linkIntent: 'selected', displayMode: 'related_only' },
  [LINK_TYPES.DEPARTS_FROM]: { linkIntent: 'selected', displayMode: 'related_only' },
  [LINK_TYPES.ARRIVES_AT]: { linkIntent: 'selected', displayMode: 'related_only' },
  [LINK_TYPES.ALLOCATES_COST]: { linkIntent: 'selected', displayMode: 'related_only' }
};

export const DEFAULT_LINK_REGISTRY_CONFIG = {
  schemaVersion: 1,
  writeMode: 'legacy_only',
  readMode: 'legacy_only',
  extractorVersion: null,
  extractorStats: null,
  updatedAt: null
};

/** Phase 4 defaults for new trips and imports without `_meta` (after tp1 + Tassie pilots). */
export const NEW_TRIP_LINK_REGISTRY_CONFIG = {
  schemaVersion: 1,
  writeMode: 'registry_only',
  readMode: 'registry_only',
  extractorVersion: null,
  extractorStats: null,
  updatedAt: null
};

const VALID_WRITE_MODES = ['legacy_only', 'dual_write', 'registry_only'];
const VALID_READ_MODES = ['legacy_only', 'registry_primary', 'registry_only'];

/**
 * Resolves config for new trips/imports. Keeps absent-doc default (`DEFAULT_LINK_REGISTRY_CONFIG`) unchanged.
 * Optional dev overrides: REACT_APP_LINK_REGISTRY_WRITE_MODE / REACT_APP_LINK_REGISTRY_READ_MODE.
 * @returns {typeof NEW_TRIP_LINK_REGISTRY_CONFIG}
 */
export function resolveNewTripLinkRegistryConfig() {
  const config = { ...NEW_TRIP_LINK_REGISTRY_CONFIG };
  const envWrite = process.env.REACT_APP_LINK_REGISTRY_WRITE_MODE;
  const envRead = process.env.REACT_APP_LINK_REGISTRY_READ_MODE;

  if (envWrite && VALID_WRITE_MODES.includes(envWrite)) {
    config.writeMode = envWrite;
  }
  if (envRead && VALID_READ_MODES.includes(envRead)) {
    config.readMode = envRead;
  }

  return config;
}

/**
 * @param {string} entityType
 * @returns {boolean}
 */
export function isRegisteredEntityType(entityType) {
  const config = ENTITY_TYPE_REGISTRY[entityType];
  return Boolean(config && config.enabled !== false);
}

/**
 * Maps legacy/subtype entity ids (e.g. document connection `hotel`) to registry types.
 * @param {string} entityType
 * @returns {string}
 */
export function normalizeRegistryEntityType(entityType) {
  if (isRegisteredEntityType(entityType)) {
    return entityType;
  }
  const alias = ENTITY_TYPE_ALIASES[entityType];
  if (alias && isRegisteredEntityType(alias)) {
    return alias;
  }
  return entityType;
}

/**
 * @param {string} entityType
 * @returns {{ collection: string, sortRank: number }}
 */
export function getEntityTypeConfig(entityType) {
  const config = ENTITY_TYPE_REGISTRY[entityType];
  if (!config || config.enabled === false) {
    throw new Error(`Unsupported or disabled entity type: ${entityType}`);
  }
  return config;
}

/**
 * @param {string} linkType
 * @returns {{ linkIntent: string, displayMode: string }}
 */
export function getLinkTypeDefaults(linkType) {
  return LINK_TYPE_DEFAULTS[linkType] || { linkIntent: 'selected', displayMode: 'related_only' };
}
